import { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { olayBildir, tokenGovdesi } from '../utils/olayBildir';

// Son doğrulanmış kullanıcı — sunucuya anlık ulaşılamazsa ekran bununla açık kalır (çıkışta silinir)
const SON_KULLANICI = 'yetsan_son_kullanici';

// İlk render/efektlerden ÖNCE global Authorization header'ı kur (senkron) — header'sız
// çağrıların ilk yüklemede de yetkili gitmesi için.
try {
  const _bootTok = localStorage.getItem('token');
  if (_bootTok) axios.defaults.headers.common['Authorization'] = `Bearer ${_bootTok}`;
} catch { /* ssr / storage yok */ }

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const verifyTekrarRef = useRef(null);
  const [loading, setLoading] = useState(true);

  // 🔑 Global axios Authorization header — token varsa TÜM isteklere otomatik eklenir.
  // (Bazı sayfalar çağrılarına manuel header eklemiyordu → backend "yetkiniz yok" dönüyordu;
  //  transfer/sipariş/teklif onay vb. Bu, kök çözüm: tek yerde garanti.)
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);
  // 💰 Müşteri iskonto oranı - localStorage'dan başlangıç değeri oku
  const [musteriIskonto, setMusteriIskonto] = useState(() => {
    const saved = localStorage.getItem('musteriIskonto');
    return saved ? parseFloat(saved) : 0;
  });

  // 💰 Grup bazlı iskonto map'i: { 'ITHAL': 20, 'YETSAN': 30, ... }
  // groupCode ile eşleşen ürünlere genel yerine bu iskonto uygulanır.
  const [grupIskontolar, setGrupIskontolar] = useState(() => {
    try {
      const saved = localStorage.getItem('grupIskontolar');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  
  // 🔴 Force logout kontrolü için
  const lastForceLogoutCheck = useRef(localStorage.getItem('lastForceLogoutTimestamp') || '');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // 🔴 Force logout kontrolü
  const checkForceLogout = useCallback(async () => {
    if (!token) return;
    
    try {
      const res = await axios.get(`${BACKEND_URL}/api/sistem/versiyon`, {
        timeout: 5000
      });
      
      if (res.data.success && res.data.timestamp) {
        const serverTimestamp = res.data.timestamp;
        // Önce localStorage'dan oku, yoksa ref'ten (ilk yükleme için)
        const localTimestamp = localStorage.getItem('lastForceLogoutTimestamp') || lastForceLogoutCheck.current;
        
        // Eğer local'de timestamp VARSA ve server'dakinden FARKLI ise = force logout yapıldı
        if (localTimestamp && localTimestamp !== serverTimestamp) {
          console.log('[Auth] Force logout algılandı:', res.data.mesaj);
          console.log('[Auth] Local:', localTimestamp, 'Server:', serverTimestamp);
          toast.warning(res.data.mesaj || 'Sistem güncellemesi - Lütfen tekrar giriş yapın');
          logout();
          return;
        }
        
        // Timestamp'i kaydet (ilk sefer veya aynı ise güncelle)
        lastForceLogoutCheck.current = serverTimestamp;
        localStorage.setItem('lastForceLogoutTimestamp', serverTimestamp);
      }
    } catch (err) {
      // Sessizce devam et
    }
  }, [token, BACKEND_URL]);

  useEffect(() => {
    if (token) {
      verifyToken();
      // Force logout kontrolü DEVRE DIŞI - sürekli logout sorunu çözülene kadar
      // checkForceLogout();
    } else {
      setLoading(false);
    }
  }, []);
  
  // Periyodik force logout kontrolü DEVRE DIŞI
  // Sorun: localStorage timestamp karşılaştırması sürekli farklı çıkıyor
  // useEffect(() => {
  //   if (!token) return;
  //   
  //   const interval = setInterval(() => {
  //     checkForceLogout();
  //   }, 60000);
  //   
  //   return () => clearInterval(interval);
  // }, [token, checkForceLogout]);

  // Sunucuya ulaşılamadığında ekranı açık tutmak için son doğrulanmış kullanıcı; yoksa token gövdesi.
  // Yetki her istekte SUNUCUDA yeniden doğrulanır — bu yalnız arayüzün açık kalmasını sağlar.
  const oturumuKoru = (sebep, mesaj) => {
    console.warn('[Auth] verify geçici hata, oturum korunuyor:', sebep, mesaj || '');
    olayBildir('oturum_dogrulama_gecici_hata', { sebep, mesaj: String(mesaj || '').slice(0, 200) });
    let yedek = null;
    try { yedek = JSON.parse(localStorage.getItem(SON_KULLANICI) || 'null'); } catch { yedek = null; }
    if (!yedek) {
      const g = tokenGovdesi(token);
      if (g) yedek = { ...g, id: g.user_id, _tokenden: true };
    }
    if (yedek) setUser(yedek);
    // Sessizce tekrar dene — sunucu toparlanınca güncel kullanıcı bilgisi gelir
    if (!verifyTekrarRef.current) {
      verifyTekrarRef.current = setTimeout(() => { verifyTekrarRef.current = null; verifyToken(); }, 15000);
    }
  };

  const verifyToken = async () => {
    console.log('[Auth] verifyToken başladı, token:', token?.substring(0, 20) + '...');
    try {
      const response = await axios.post(`${API}/auth/verify`, { token }, { timeout: 20000 });
      console.log('[Auth] verify response:', response.data);
      if (response.data.success) {
        const userData = response.data.user;
        console.log('[Auth] User data set ediliyor:', userData);
        setUser(userData);
        
        // 💰 Müşteri ise iskonto oranını ayarla (verify response'tan)
        if (userData.role === 'musteri' || userData.role === 'client') {
          const iskonto = userData.iskonto_orani || 0;
          console.log('[Auth] Verify - Müşteri iskonto oranı:', iskonto, '%');
          setMusteriIskonto(iskonto);
          localStorage.setItem('musteriIskonto', iskonto.toString());
          // 🆕 Grup iskontolarını da doğrudan verify response'tan al
          const grupIsk = userData.grup_iskontolar || {};
          console.log('[Auth] Verify - Grup iskontoları:', grupIsk);
          setGrupIskontolar(grupIsk);
          try { localStorage.setItem('grupIskontolar', JSON.stringify(grupIsk)); } catch { /* noop */ }
          // NOT: Yedek fetchMusteriIskonto ÇAĞRISI KALDIRILDI. verify yanıtı zaten
          // tarih-sıkı (baslangic/bitis) + musteri_id ObjectId&string güvenli hesaplıyor
          // (auth.py:708). Yedek uç (musteri-iskontolar-tam) STRING-only → ObjectId saklı
          // iskontoyu kaçırıp doğru değeri 0'a düşürebiliyordu. Redundant + riskli çağrı çıkarıldı.
        }
        try { localStorage.setItem(SON_KULLANICI, JSON.stringify(userData)); } catch { /* noop */ }
      } else if (response.data.gecici) {
        // Sunucu doğrulayamadı ama token'ı REDDETMEDİ (ör. veritabanı anlık yanıt vermedi) → oturum korunur
        oturumuKoru('gecici_dogrulama_hatasi', response.data.error);
      } else {
        console.log('[Auth] verify başarısız, logout yapılıyor');
        olayBildir('zorunlu_cikis', { sebep: 'verify_red', hata: response.data.error || '' });
        logout();
      }
    } catch (error) {
      // 2026-10-12: eskiden HER hata (ağ, zaman aşımı, 502, 500) çıkış yaptırıyordu. Sunucu bir an donunca
      // (ör. Logo'ya senkron bağlantı 40 sn kilitledi) sayfayı yenileyen herkes dışarı atılıyordu. Çıkış YALNIZ
      // sunucu token'ı açıkça reddederse (401/403); geçici hatada kullanıcı içeride kalır, doğrulama tekrar denenir.
      const durum = error?.response?.status;
      if (durum === 401 || durum === 403) {
        olayBildir('zorunlu_cikis', { sebep: `verify_${durum}`, kod: error?.response?.data?.code || '' });
        logout();
      } else {
        oturumuKoru(durum ? `verify_${durum}` : (error?.code === 'ECONNABORTED' ? 'verify_zaman_asimi' : 'verify_ag_hatasi'),
                    error?.message);
      }
    } finally {
      console.log('[Auth] loading false yapılıyor');
      setLoading(false);
    }
  };

  // Müşteri iskonto oranını çek (fallback - endpoint yoksa sessizce devam et)
  // Hem genel hem grup bazlı iskontoları getirir.
  const fetchMusteriIskonto = async (musteriId) => {
    try {
      // Yeni: tam iskonto bilgisi (genel + grup)
      const res = await axios.get(`${API}/panel/musteri-iskontolar-tam/${musteriId}`);
      if (res.data.success) {
        const iskonto = res.data.genel_iskonto ?? res.data.iskonto_orani ?? 0;
        const grupIsk = res.data.grup_iskontolar || {};
        setMusteriIskonto(iskonto);
        setGrupIskontolar(grupIsk);
        localStorage.setItem('musteriIskonto', iskonto.toString());
        localStorage.setItem('grupIskontolar', JSON.stringify(grupIsk));
        console.log('[Auth] Genel iskonto:', iskonto, '% — Grup iskontolar:', grupIsk);
        return;
      }
    } catch (err) {
      // tam endpoint yoksa eski endpoint'e fallback
      if (err.response?.status !== 404) {
        console.log('[Auth] Tam iskonto API hatası, eski endpoint deneniyor:', err.message);
      }
    }
    try {
      const res = await axios.get(`${API}/panel/musteri-genel-iskonto/${musteriId}`);
      if (res.data.success) {
        const iskonto = res.data.iskonto_orani || 0;
        setMusteriIskonto(iskonto);
        localStorage.setItem('musteriIskonto', iskonto.toString());
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        console.log('[Auth] İskonto API hatası:', err.message);
      }
    }
  };

  const login = async (email, password, extraPayload = {}) => {
    try {
      // 🔐 Cihaz fingerprint + tür otomatik ekle (device-trust sistemi için)
      let fingerprint = '';
      let device_type = 'web';
      try {
        const { getDeviceFingerprint, getDeviceType } = await import('../utils/deviceFingerprint');
        fingerprint = await getDeviceFingerprint();
        device_type = getDeviceType();
      } catch (fpErr) { /* fingerprint olmadan da denesin */ }

      const payload = { email, password, fingerprint, device_type, ...extraPayload };
      // Zaman aşımı: sunucu donarsa giriş ekranı sonsuza dek beklemesin, kullanıcıya ne olduğu söylensin
      const response = await axios.post(`${API}/auth/login`, payload, { timeout: 30000 });
      
      if (response.data.success) {
        const { token, user } = response.data;
        setToken(token);
        setUser(user);
        localStorage.setItem('token', token);
        try { localStorage.setItem(SON_KULLANICI, JSON.stringify(user)); } catch { /* noop */ }
        // 👤 Kullanıcı bilgilerini localStorage'a kaydet
        localStorage.setItem('userName', user.name || user.userName || user.email || '');
        localStorage.setItem('email', user.email || '');
        localStorage.setItem('firma', user.firma || '');
        localStorage.setItem('userId', user.id || user.user_id || user._id || '');
        localStorage.setItem('cari_kodu', user.cari_kodu || '');
        localStorage.setItem('cari_unvan', user.cari_unvan || '');  // Ticari ünvan
        localStorage.setItem('panel_kodu', user.panel_kodu || '');  // 6 haneli müşteri no
        
        // 💰 Müşteri ise iskonto oranını ayarla (login response'tan direkt al)
        if (user.role === 'musteri' || user.role === 'client') {
          const iskonto = user.iskonto_orani || 0;
          console.log('[Auth] Login - Müşteri iskonto oranı (response):', iskonto, '%');
          setMusteriIskonto(iskonto);
          localStorage.setItem('musteriIskonto', iskonto.toString());

          // 🆕 Grup iskontoları — login response'tan direkt al (ITHAL/YETSAN vb)
          const grupIsk = user.grup_iskontolar || {};
          console.log('[Auth] Login - Grup iskontoları:', grupIsk);
          setGrupIskontolar(grupIsk);
          try { localStorage.setItem('grupIskontolar', JSON.stringify(grupIsk)); } catch { /* noop */ }

          // NOT: Yedek fetchMusteriIskonto ÇAĞRISI KALDIRILDI (redundant + riskli).
          // login yanıtı iskontoyu tarih-sıkı hesaplıyor; verify (mount'ta) ObjectId-güvenli
          // olarak tazeler. Yedek uç STRING-only olduğu için değeri 0'a düşürebiliyordu.
        }

        return { success: true, user, must_change_password: !!response.data.must_change_password };
      } else {
        // 🔐 Device trust akışı tetikleyici durum kodları
        if (response.data.device_trust_required) {
          return {
            success: false,
            device_trust_required: true,
            status: response.data.status,  // new_device | pending_approval | blocked_concurrent_ip | limit_exceeded | pending_admin
            error: response.data.error,
            user_id: response.data.user_id,
            email: response.data.email
          };
        }
        // 🆕 Mesai dışı erişim (Feb 2026) — staff/firma rolünde mesai sonrası giriş
        if (response.data.mesai_disi) {
          return {
            success: false,
            mesai_disi: true,
            error: response.data.error,
            user_id: response.data.user_id,
            email: response.data.email,
            neden: response.data.neden,
          };
        }
        return {
          success: false,
          error: response.data.error,
          code: response.data.code,
          email: response.data.email,
          message: response.data.message,
        };
      }
    } catch (error) {
      const durum = error?.response?.status;
      const zamanAsimi = error?.code === 'ECONNABORTED';
      olayBildir('giris_basarisiz_baglanti', { durum: durum || null, zaman_asimi: zamanAsimi, mesaj: String(error?.message || '').slice(0, 200) });
      if (zamanAsimi || !durum || durum >= 500) {
        return { success: false, error: 'Sunucu şu an yanıt vermiyor. Birkaç saniye sonra tekrar deneyin; sorun sistem ekibine bildirildi.' };
      }
      return { success: false, error: 'Bağlantı hatası' };
    }
  };

  // 🔑 Müşteri OTP (şifresiz) giriş — kod iste
  const otpIste = async (email) => {
    try {
      const r = await axios.post(`${API}/auth/musteri-otp-iste`, { email });
      return { success: !!r.data?.success, message: r.data?.message };
    } catch (e) {
      return { success: false, error: e.response?.data?.error || 'Bağlantı hatası' };
    }
  };

  // Giriş tamamlama (token + kullanıcı state + localStorage + iskonto)
  const otpLoginUygula = (token, user) => {
    setToken(token);
    setUser(user);
    localStorage.setItem('token', token);
    try { localStorage.setItem(SON_KULLANICI, JSON.stringify(user)); } catch { /* noop */ }
    localStorage.setItem('userName', user.name || user.email || '');
    localStorage.setItem('email', user.email || '');
    localStorage.setItem('firma', user.firma || '');
    localStorage.setItem('userId', user.id || '');
    localStorage.setItem('cari_kodu', user.cari_kodu || '');
    localStorage.setItem('cari_unvan', user.cari_unvan || '');
    localStorage.setItem('panel_kodu', user.panel_kodu || '');
    const iskonto = user.iskonto_orani || 0;
    setMusteriIskonto(iskonto);
    localStorage.setItem('musteriIskonto', iskonto.toString());
    const grupIsk = user.grup_iskontolar || {};
    setGrupIskontolar(grupIsk);
    try { localStorage.setItem('grupIskontolar', JSON.stringify(grupIsk)); } catch { /* noop */ }
    // NOT: Yedek fetchMusteriIskonto çağrısı kaldırıldı (redundant + STRING-only riski).
    // Yanıttaki iskonto otoritatif; verify (mount) ObjectId-güvenli tazeler.
  };

  // 🔑 Müşteri OTP ile giriş — e-posta + kod
  const otpGiris = async (email, otp) => {
    try {
      // Cihaz fingerprint'i ekle ki ilk OTP girişinde cihaz güvenilir kaydedilsin
      // (sonraki şifreli girişte ikinci cihaz-OTP'si çıkmasın)
      let fingerprint = '';
      let device_type = 'web';
      let fp_extra;
      try {
        const { getDeviceFingerprint, getDeviceType, getDeviceFingerprintExtras } = await import('../utils/deviceFingerprint');
        fingerprint = await getDeviceFingerprint();
        device_type = getDeviceType();
        try { fp_extra = getDeviceFingerprintExtras(); } catch { /* opsiyonel */ }
      } catch (fpErr) { /* fingerprint olmadan da devam */ }
      const r = await axios.post(`${API}/auth/musteri-otp-giris`, { email, otp, fingerprint, device_type, fp_extra });
      if (r.data?.success) {
        const { token, user } = r.data;
        const mustChange = !!(r.data?.must_change_password || user.must_change_password);
        if (mustChange) {
          // ⚠️ Girişi HENÜZ tamamlama: token set edilirse App /login'den panele
          // yönlendirir ve şifre belirleme ekranı render olamaz. Token'ı döndür;
          // şifre belirlenince otpLoginUygula ile giriş tamamlanır.
          return { success: true, user, must_change_password: true, token };
        }
        otpLoginUygula(token, user);
        return { success: true, user, must_change_password: false };
      }
      return { success: false, error: r.data?.error, code: r.data?.code };
    } catch (e) {
      return { success: false, error: e.response?.data?.error || 'Bağlantı hatası' };
    }
  };

  // 🔑 Müşteri ilk girişte (OTP sonrası) kendi şifresini belirler.
  // token: OTP girişinde dönen (henüz uygulanmamış) token — Authorization header için.
  const sifreBelirle = async (yeniSifre, token) => {
    try {
      const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
      const r = await axios.post(`${API}/auth/musteri-sifre-belirle`, { yeni_sifre: yeniSifre }, cfg);
      if (r.data?.success) return { success: true };
      return { success: false, error: r.data?.error };
    } catch (e) {
      return { success: false, error: e.response?.data?.error || 'Bağlantı hatası' };
    }
  };

  const logout = () => {
    // 🆕 Analytics session'ı sonlandır
    const sessionId = localStorage.getItem('analytics_session_id');
    if (sessionId) {
      // sendBeacon ile güvenilir gönderim (sayfa kapansa bile çalışır)
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      navigator.sendBeacon(
        `${BACKEND_URL}/api/analytics/session/end`,
        JSON.stringify({
          session_id: sessionId,
          reason: 'user_logout'
        })
      );
      // localStorage temizle
      localStorage.removeItem('analytics_session_id');
      localStorage.removeItem('analytics_intel_product');
      localStorage.removeItem('analytics_intel_search');
      localStorage.removeItem('analytics_intel_copy');
    }
    
    if (verifyTekrarRef.current) { clearTimeout(verifyTekrarRef.current); verifyTekrarRef.current = null; }
    setToken(null);
    setUser(null);
    setMusteriIskonto(0);
    localStorage.removeItem('token');
    localStorage.removeItem(SON_KULLANICI);
    // 👤 Kullanıcı bilgilerini de temizle
    localStorage.removeItem('userName');
    localStorage.removeItem('email');
    localStorage.removeItem('firma');
    localStorage.removeItem('userId');
    localStorage.removeItem('cari_kodu');
    localStorage.removeItem('cari_unvan');
    localStorage.removeItem('panel_kodu');
    localStorage.removeItem('musteriIskonto');
    localStorage.removeItem('grupIskontolar');
    // 🧹 Yetki/menü cache'lerini (yetki_cache_<uid>) temizle → shared cihazda stale yetki
    // sızıntısı olmasın (YetkiContext localStorage hydrate ile yazıyor).
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith('yetki_cache_')) localStorage.removeItem(k);
      }
    } catch { /* noop */ }
  };

  // YENİ: Role bazlı yetki kontrolü
  const hasRole = (allowedRoles) => {
    if (!user || !user.role) return false;
    
    // allowedRoles string veya array olabilir
    const roles = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];
    return roles.includes(user.role);
  };

  // YENİ: Admin kontrolü
  const isAdmin = ['admin', 'superadmin', 'yetsanadmin'].includes(user?.role);

  // SuperAdmin kontrolü
  const isSuperAdmin = ['superadmin', 'yetsanadmin'].includes(user?.role);

  // Müdür kontrolü
  const isMudur = user?.role === 'mudur';

  // YENİ: Firma kullanıcısı mı kontrolü
  const isFirmaUser = () => {
    // Firma bazlı kısıtlamalı roller: firma, mudur, firma_kullanicisi
    return ['firma', 'mudur', 'firma_kullanicisi'].includes(user?.role);
  };

  // YENİ: Müşteri kullanıcısı mı kontrolü
  const isMusteriUser = () => {
    return user?.role === 'musteri' || user?.role === 'client';
  };

  // YENİ: Firma erişim kontrolü
  const canAccessFirma = (firmaKodu) => {
    if (!user) return false;
    
    // Admin ve superadmin her firmaya erişebilir
    if (['admin', 'superadmin'].includes(user.role)) return true;
    
    // Firma bazlı kısıtlamalı roller sadece kendi firmasına erişebilir
    if (['firma', 'mudur', 'firma_kullanicisi'].includes(user.role)) {
      return user.firma === firmaKodu;
    }
    
    // Müşteri kullanıcısı hiçbir firmaya erişemez
    return false;
  };

  // YENİ: Kullanıcının firma kodunu al
  const getUserFirma = () => {
    return user?.firma || null;
  };

  // Eski hasPermission fonksiyonu - geriye dönük uyumluluk için
  const hasPermission = (requiredRole) => {
    return hasRole(requiredRole);
  };

  // İskonto ile fiyat hesapla - grup-aware
  // Öncelik: Grup iskontosu (ürünün groupCode'una eşleşen) > Genel iskonto
  // urun parametresi opsiyoneldir; geriye uyumluluk için sadece liste fiyatla çağrılabilir.
  const hesaplaIskontoluFiyat = (listeFiyat, urun = null) => {
    if (!listeFiyat || listeFiyat <= 0) return listeFiyat;
    // 🎯 Backend'in hesapladığı ETKİN iskonto (ürün > kategori > grup > genel) varsa onu kullan
    if (urun && urun.musteri_iskonto_orani !== undefined && urun.musteri_iskonto_orani !== null && urun.musteri_iskonto_orani > 0) {
      return listeFiyat * (1 - Number(urun.musteri_iskonto_orani) / 100);
    }
    // Grup bazlı override
    if (urun && grupIskontolar) {
      const grup = (urun.groupCode || urun.grup || urun.producerCode || '').toString().trim().toUpperCase();
      if (grup && grupIskontolar[grup] !== undefined && grupIskontolar[grup] > 0) {
        return listeFiyat * (1 - grupIskontolar[grup] / 100);
      }
    }
    // Genel iskonto fallback
    if (!musteriIskonto || musteriIskonto <= 0) return listeFiyat;
    return listeFiyat * (1 - musteriIskonto / 100);
  };

  // Bir ürün için efektif iskonto oranını döndürür (gösterim için: %20 İNDİRİM)
  const getEffektifIskontoOrani = (urun = null) => {
    // 🎯 Backend'in hesapladığı etkin iskonto varsa onu göster
    if (urun && urun.musteri_iskonto_orani !== undefined && urun.musteri_iskonto_orani !== null && urun.musteri_iskonto_orani > 0) {
      return Number(urun.musteri_iskonto_orani);
    }
    if (urun && grupIskontolar) {
      const grup = (urun.groupCode || urun.grup || urun.producerCode || '').toString().trim().toUpperCase();
      if (grup && grupIskontolar[grup] !== undefined && grupIskontolar[grup] > 0) {
        return grupIskontolar[grup];
      }
    }
    return musteriIskonto || 0;
  };

  const value = {
    user,
    setUser,
    token,
    setToken,
    loading,
    login,
    otpIste,
    otpGiris,
    sifreBelirle,
    otpLoginUygula,
    logout,
    hasPermission,
    hasRole,
    isAdmin,
    isSuperAdmin,
    isMudur,
    isFirmaUser,
    isMusteriUser,
    canAccessFirma,
    getUserFirma,
    isAuthenticated: !!user,
    musteriIskonto,
    setMusteriIskonto,
    grupIskontolar,
    setGrupIskontolar,
    hesaplaIskontoluFiyat,
    getEffektifIskontoOrani,
    fetchMusteriIskonto
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
